import {
  AdminDashboardData,
  DashboardMetrics,
  PaginatedFailedTransactions,
  FailedTransactionDetail,
  FailedTransactionFilters,
  CashFlowReport,
  ReprocessTransactionResponse,
  ApiResponse,
} from "../types/admin.types";
import { authService } from "./authService";

const API_URL =
  process.env.REACT_APP_API_URL ;

class AdminService {
  /**
   * Obtiene headers con autenticación
   */
  private getAuthHeaders(): HeadersInit {
    return authService.getAuthHeaders();
  }

  /**
   * Obtiene todos los datos del dashboard
   */
  async getDashboardData(): Promise<AdminDashboardData> {
    try {
      const response = await fetch(`${API_URL}/Admin/dashboard`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const apiResponse: ApiResponse<AdminDashboardData> =
        await response.json();

      if (!apiResponse.Success) {
        throw new Error(
          apiResponse.Message || "Error al obtener datos del dashboard",
        );
      }

      return apiResponse.Data;
    } catch (error: any) {
      console.error("Error fetching dashboard data:", error);
      throw new Error(error.message || "Error al comunicarse con el servidor");
    }
  }

  /**
   * Obtiene métricas generales del sistema
   */
  async getMetrics(): Promise<DashboardMetrics> {
    try {
      const response = await fetch(`${API_URL}/Admin/metrics`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const apiResponse: ApiResponse<DashboardMetrics> = await response.json();

      if (!apiResponse.Success) {
        throw new Error(apiResponse.Message || "Error al obtener métricas");
      }

      return apiResponse.Data;
    } catch (error: any) {
      console.error("Error fetching metrics:", error);
      throw new Error(error.message || "Error al comunicarse con el servidor");
    }
  }

  /**
   * Obtiene transacciones fallidas con filtros y paginación
   */
  async getFailedTransactions(
    filters?: FailedTransactionFilters,
  ): Promise<PaginatedFailedTransactions> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        if (filters.Status) params.append("status", filters.Status);
        if (filters.StartDate) params.append("startDate", filters.StartDate);
        if (filters.EndDate) params.append("endDate", filters.EndDate);
        if (filters.Placa) params.append("placa", filters.Placa);
        if (filters.CustomerIdentification)
          params.append(
            "customerIdentification",
            filters.CustomerIdentification,
          );
        if (filters.MinRetryCount !== undefined)
          params.append("minRetryCount", filters.MinRetryCount.toString());
        if (filters.PageNumber)
          params.append("pageNumber", filters.PageNumber.toString());
        if (filters.PageSize)
          params.append("pageSize", filters.PageSize.toString());
      }

      const url = `${API_URL}/Admin/failed-transactions${params.toString() ? "?" + params.toString() : ""}`;
      const response = await fetch(url, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const apiResponse: ApiResponse<PaginatedFailedTransactions> =
        await response.json();

      if (!apiResponse.Success) {
        throw new Error(
          apiResponse.Message || "Error al obtener transacciones fallidas",
        );
      }

      return apiResponse.Data;
    } catch (error: any) {
      console.error("Error fetching failed transactions:", error);
      throw new Error(error.message || "Error al comunicarse con el servidor");
    }
  }

  /**
   * Obtiene el detalle completo de una transacción fallida
   */
  async getFailedTransactionDetail(
    transactionId: number,
  ): Promise<FailedTransactionDetail> {
    try {
      const response = await fetch(
        `${API_URL}/Admin/failed-transactions/${transactionId}`,
        {
          headers: this.getAuthHeaders(),
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Transacción no encontrada");
        }
        throw new Error(`Error HTTP ${response.status}`);
      }

      const apiResponse: ApiResponse<FailedTransactionDetail> =
        await response.json();

      if (!apiResponse.Success) {
        throw new Error(
          apiResponse.Message || "Error al obtener detalle de transacción",
        );
      }

      return apiResponse.Data;
    } catch (error: any) {
      console.error(`Error fetching transaction ${transactionId}:`, error);
      throw new Error(error.message || "Error al comunicarse con el servidor");
    }
  }

  /**
   * Reprocesa una transacción fallida manualmente
   */
  async reprocessTransaction(
    transactionId: number,
    forceReprocess: boolean = false,
  ): Promise<ReprocessTransactionResponse> {
    try {
      const response = await fetch(
        `${API_URL}/Admin/failed-transactions/${transactionId}/reprocess?forceReprocess=${forceReprocess}`,
        {
          method: "POST",
          headers: this.getAuthHeaders(),
        },
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.Message || `Error HTTP ${response.status}`);
      }

      const apiResponse: ApiResponse<ReprocessTransactionResponse> =
        await response.json();

      if (!apiResponse.Success) {
        throw new Error(
          apiResponse.Message || "Error al reprocesar transacción",
        );
      }

      return apiResponse.Data;
    } catch (error: any) {
      console.error(`Error reprocessing transaction ${transactionId}:`, error);
      throw new Error(error.message || "Error al comunicarse con el servidor");
    }
  }

  /**
   * Obtiene reporte de flujo de caja
   */
  async getCashFlowReport(
    period: "daily" | "weekly" | "monthly" | "custom",
    startDate?: string,
    endDate?: string,
  ): Promise<CashFlowReport> {
    try {
      const params = new URLSearchParams();
      params.append("period", period);

      if (period === "custom") {
        if (!startDate || !endDate) {
          throw new Error(
            "Se requieren fechas de inicio y fin para período personalizado",
          );
        }
        params.append("startDate", startDate);
        params.append("endDate", endDate);
      }

      const response = await fetch(
        `${API_URL}/Admin/cash-flow?${params.toString()}`,
        {
          headers: this.getAuthHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const apiResponse: ApiResponse<CashFlowReport> = await response.json();

      if (!apiResponse.Success) {
        throw new Error(
          apiResponse.Message || "Error al obtener reporte de flujo de caja",
        );
      }

      return apiResponse.Data;
    } catch (error: any) {
      console.error("Error fetching cash flow report:", error);
      throw new Error(error.message || "Error al comunicarse con el servidor");
    }
  }

  /**
   * Obtiene estadísticas de los últimos 30 días
   */
  async getLast30DaysStats(): Promise<CashFlowReport> {
    try {
      const response = await fetch(`${API_URL}/Admin/stats/last-30-days`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const apiResponse: ApiResponse<CashFlowReport> = await response.json();

      if (!apiResponse.Success) {
        throw new Error(apiResponse.Message || "Error al obtener estadísticas");
      }

      return apiResponse.Data;
    } catch (error: any) {
      console.error("Error fetching last 30 days stats:", error);
      throw new Error(error.message || "Error al comunicarse con el servidor");
    }
  }

  /**
   * Obtiene la configuración actual del sistema
   */
  async getSystemSettings(): Promise<any> {
    try {
      const response = await fetch(`${API_URL}/Admin/settings`, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const apiResponse: ApiResponse<any> = await response.json();

      if (!apiResponse.Success) {
        throw new Error(
          apiResponse.Message || "Error al obtener configuración",
        );
      }

      return apiResponse.Data;
    } catch (error: any) {
      console.error("Error fetching system settings:", error);
      throw new Error(error.message || "Error al comunicarse con el servidor");
    }
  }

  /**
   * Actualiza la configuración de pagos
   */
  async updatePaymentSettings(settings: {
    excludeCurrentYearFromPayments: boolean;
    requireFullPayment: boolean;
  }): Promise<any> {
    try {
      const response = await fetch(`${API_URL}/Admin/settings/payment`, {
        method: "PUT",
        headers: {
          ...this.getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paymentSettings: settings,
          updatedBy: authService.getCurrentUser()?.Email || "admin",
        }),
      });

      if (!response.ok) {
        throw new Error(`Error HTTP ${response.status}`);
      }

      const apiResponse: ApiResponse<any> = await response.json();

      if (!apiResponse.Success) {
        throw new Error(
          apiResponse.Message || "Error al actualizar configuración",
        );
      }

      return apiResponse.Data;
    } catch (error: any) {
      console.error("Error updating payment settings:", error);
      throw new Error(error.message || "Error al comunicarse con el servidor");
    }
  }
}

export const adminService = new AdminService();
